import { useState, useEffect } from 'react';
import {
  updateProject,
  saveProjectBible,
  BIBLE_COLOR_SUGGESTIONS,
  type ContentProject,
  type ProjectBible,
} from '@/lib/contentStudio';

interface Props {
  project: ContentProject;
  onSaved: (bible: ProjectBible) => void;
}

const FIELDS: { key: keyof ProjectBible; label: string; icon: string; placeholder: string; rows: number }[] = [
  { key: 'places', label: 'Lieux', icon: 'ri-map-pin-2-line', placeholder: 'Kinshasa, palais présidentiel, camp militaire…', rows: 3 },
  { key: 'era', label: 'Époque', icon: 'ri-history-line', placeholder: 'Années 1960-1990, époque contemporaine…', rows: 2 },
  { key: 'style', label: 'Style visuel', icon: 'ri-palette-line', placeholder: 'Documentaire réaliste, cinématographique, archive noir et blanc…', rows: 2 },
  { key: 'visualTone', label: 'Ambiance / direction artistique', icon: 'ri-contrast-2-line', placeholder: 'Lumière naturelle, tons chauds, grain argentique, solennel…', rows: 2 },
  { key: 'rules', label: 'Règles visuelles', icon: 'ri-list-check-2', placeholder: 'Toujours garder le même uniforme ; jamais de texte à l\u2019écran…', rows: 3 },
  { key: 'vocabulary', label: 'Vocabulaire / ton', icon: 'ri-chat-quote-line', placeholder: 'Ton neutre et factuel, vocabulaire historique précis…', rows: 2 },
];

/**
 * Bible du projet : référentiel partagé (lieux, époque, style, couleurs,
 * règles) utilisé pour garder une cohérence visuelle entre toutes les scènes.
 */
export default function BiblePanel({ project, onSaved }: Props) {
  const initial = (project.bible || {}) as ProjectBible;
  const [draft, setDraft] = useState<ProjectBible>(initial);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customColor, setCustomColor] = useState('');

  const colors = draft.colors || [];

  useEffect(() => {
    setDraft((project.bible || {}) as ProjectBible);
  }, [project.id, project.bible]);

  const toggleColor = (hex: string) => {
    setDraft((d) => {
      const list = d.colors || [];
      return { ...d, colors: list.includes(hex) ? list.filter((c) => c !== hex) : [...list, hex] };
    });
  };

  const addCustomColor = () => {
    const hex = customColor.trim();
    if (!/^#([0-9a-fA-F]{6})$/.test(hex)) return;
    setDraft((d) => ({ ...d, colors: [...(d.colors || []), hex.toLowerCase()] }));
    setCustomColor('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveProjectBible(project.id, initial, draft);
      // Synchronise aussi visual_context (référentiel condensé pour le moteur IA).
      const summary = [draft.style, draft.visualTone, draft.places, draft.era].filter(Boolean).join(' · ');
      if (summary) await updateProject(project.id, { visual_context: summary });
      onSaved(draft);
      setFlash(true);
      setTimeout(() => setFlash(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur d\u2019enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-book-2-line text-foreground-500"></i>Bible du projet
          </h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-2xl">
            Le référentiel créatif du projet. Lieux, époque, style et couleurs y sont définis une fois et servent de
            point de repère commun à toutes les scènes.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
        >
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          Enregistrer la bible
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.rows > 2 ? 'sm:col-span-2' : ''}>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5 flex items-center gap-1.5">
                <i className={`${f.icon} text-foreground-400`}></i>{f.label}
              </label>
              <textarea
                value={(draft[f.key] as string) || ''}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                rows={f.rows}
                placeholder={f.placeholder}
                className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-accent-300 resize-none"
              />
            </div>
          ))}
        </div>

        {/* Palette de couleurs */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 h-fit">
          <label className="block text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
            <i className="ri-palette-line text-foreground-400"></i>Palette de couleurs
          </label>
          <p className="text-[11px] text-foreground-400 mb-3">
            Les teintes directrices du projet (décor, costumes, étalonnage).
          </p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {BIBLE_COLOR_SUGGESTIONS.map((hex) => {
              const active = colors.includes(hex.toLowerCase()) || colors.includes(hex);
              return (
                <button
                  key={hex}
                  type="button"
                  onClick={() => toggleColor(hex)}
                  title={hex}
                  style={{ backgroundColor: hex }}
                  className={`w-7 h-7 rounded-md border transition-transform cursor-pointer hover:scale-110 ${active ? 'border-foreground-900 ring-2 ring-accent-300' : 'border-black/10'}`}
                ></button>
              );
            })}
          </div>

          {colors.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {colors.map((hex, i) => (
                <span key={`${hex}-${i}`} className="inline-flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded-full bg-background-100 text-[10px] text-foreground-700">
                  <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ backgroundColor: hex }}></span>
                  <span className="font-mono">{hex}</span>
                  <button
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, colors: (d.colors || []).filter((_, idx) => idx !== i) }))}
                    className="text-foreground-400 hover:text-red-500 cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomColor(); } }}
              placeholder="#a1b2c3"
              className="flex-1 min-w-0 px-2.5 py-1.5 bg-background-50 border border-background-200/70 rounded-md text-xs font-mono text-foreground-800 focus:outline-none focus:border-accent-300"
            />
            <button
              type="button"
              onClick={addCustomColor}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors cursor-pointer"
              title="Ajouter la couleur"
            >
              <i className="ri-add-line"></i>
            </button>
          </div>
          {flash && (
            <p className="mt-3 text-xs text-accent-600 flex items-center gap-1">
              <i className="ri-check-line"></i>Bible enregistrée
            </p>
          )}
        </div>
      </div>
    </div>
  );
}